import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalculator, faXmark, faArrowRight,
  faChevronDown, faGripVertical, faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { FaSearch, FaBell, FaUserEdit, FaSignOutAlt, FaBars, FaColumns } from "react-icons/fa";
import * as XLSX from "xlsx";
import Loading from "./Loading";
import ContainersSidebar from "../components/ContainersSidebar";
import ProjectManager from "./ProjectManager";
import NotificationsPanel from "../components/NotificationsPanel";
import NovidadesModal from "../components/NovidadesModal";
import useNovidadesAutoShow from "../hooks/useNovidadesAutoShow";
import "./Containers.css";
import "./ContainerIncc.css";
import UserProfileCard from "../components/UserProfileCard"

// (INCCBadge, CalculadoraWindow, funções de INCC/xlsx: sem alterações — mesmas do arquivo original)
// ─────────────────────────────────────────────
const INCC_API_URL = "https://incc-api.onrender.com";
const API_SYNC_TIMEOUT = 8000;

const MESES_PT = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
];

function parseMesAno(mesAno) {
  const [mes, ano] = mesAno.split("/");
  const idx = MESES_PT.findIndex((m) => m.toLowerCase() === mes.toLowerCase());
  return new Date(parseInt(ano), idx, 1);
}

async function fetchInccDoSupabase() {
  const { data, error } = await supabase
    .from("incc_historico")
    .select("data_referencia, mes, indice, variacao_no_mes, variacao_no_ano, variacao_12_meses")
    .order("data_referencia", { ascending: true });

  if (error) throw error;

  return data.map((r) => ({
    dataRef: r.data_referencia,
    mes: r.mes,
    indice: parseFloat(r.indice),
    variacao: {
      no_mes: r.variacao_no_mes != null ? parseFloat(r.variacao_no_mes) : null,
      no_ano: r.variacao_no_ano != null ? parseFloat(r.variacao_no_ano) : null,
      doze_meses: r.variacao_12_meses != null ? parseFloat(r.variacao_12_meses) : null,
    },
  }));
}

async function sincronizarInccDaApi(ultimaMesLocal) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), API_SYNC_TIMEOUT);

    const resp = await fetch(`${INCC_API_URL}/incc`, { signal: controller.signal });
    clearTimeout(timer);

    if (!resp.ok) return;
    const json = await resp.json();
    if (!json.success || !json.data?.historico?.length) return;

    const historico = json.data.historico;
    const novos = historico.filter((r) => {
      if (!ultimaMesLocal) return true;
      return parseMesAno(r.mes) > parseMesAno(ultimaMesLocal);
    });
    if (!novos.length) return;

    const upserts = novos.map((r) => {
      const [nomeMes, ano] = r.mes.split("/");
      const mesIdx = MESES_PT.findIndex((m) => m.toLowerCase() === nomeMes.toLowerCase());
      const dataRef = `${ano}-${String(mesIdx + 1).padStart(2, "0")}-01`;
      return {
        data_referencia: dataRef,
        mes: r.mes,
        indice: r.indice,
        variacao_no_mes: r.variacao?.no_mes ?? null,
        variacao_no_ano: r.variacao?.no_ano ?? null,
        variacao_12_meses: r.variacao?.doze_meses ?? null,
      };
    });

    await supabase
      .from("incc_historico")
      .upsert(upserts, { onConflict: "data_referencia", ignoreDuplicates: true });
  } catch {
    // API offline ou timeout — ignora silenciosamente
  }
}

function downloadInccXlsx(historico) {
  const invertido = [...historico].reverse();
  const dados = invertido.map((row) => ({
    "Mês": row.mes ?? "—",
    "Índice": row.indice ?? null,
    "No mês (%)": row.variacao?.no_mes ?? null,
    "No ano (%)": row.variacao?.no_ano ?? null,
    "12 meses (%)": row.variacao?.doze_meses ?? null,
  }));
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(dados);
  ws["!cols"] = [{ wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws, "INCC-DI");
  XLSX.writeFile(wb, "historico_incc_di.xlsx");
}

function INCCBadge({ incc, historico }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const invertido = [...historico].reverse();

  return (
    <div className="incc-badge-wrapper" ref={ref}>
      <div className={`incc-badge ${open ? "incc-badge--active" : ""}`} onClick={() => setOpen((v) => !v)} title="Clique para ver o histórico completo">
        {!incc ? (
          <>
            <span className="incc-badge__label">INCC-DI</span>
            <span className="incc-badge__value">EM MANUTENÇÃO :/</span>
          </>
        ) : (
          <>
            <span className="incc-badge__label">INCC-DI · {incc.mes}</span>
            <div className="incc-badge__values">
              <span className="incc-badge__value">
                {incc.indice.toLocaleString("pt-BR", { minimumFractionDigits: 3 })}
                <span className="incc-badge__sub-label"> índice</span>
              </span>
              <span className="incc-badge__separator">|</span>
              <span className="incc-badge__value">
                {incc.variacao.no_mes > 0 ? "+" : ""}{incc.variacao.no_mes.toFixed(2)}%
                <span className="incc-badge__sub-label"> no mês</span>
              </span>
              <span className="incc-badge__separator">|</span>
              <span className="incc-badge__value">
                {incc.variacao.doze_meses.toFixed(2)}%
                <span className="incc-badge__sub-label"> 12m</span>
              </span>
              <FontAwesomeIcon icon={faChevronDown} className={`incc-badge__chevron ${open ? "incc-badge__chevron--open" : ""}`} />
            </div>
          </>
        )}
      </div>

      {open && historico.length > 0 && (
        <div className="incc-dropdown">
          <div className="incc-dropdown__header">
            <span>Histórico INCC-DI (FGV)</span>
            <div className="incc-dropdown__header-right">
              <span className="incc-dropdown__total">{historico.length} registros</span>
              <button className="incc-dropdown__xlsx-btn" onClick={(e) => { e.stopPropagation(); downloadInccXlsx(historico); }} title="Baixar tabela em Excel (.xlsx)">
                <FontAwesomeIcon icon={faFileExcel} />
                <span>Excel</span>
              </button>
            </div>
          </div>
          <div className="incc-dropdown__table-wrap">
            <table className="incc-dropdown__table">
              <thead>
                <tr><th>Mês</th><th>Índice</th><th>No mês (%)</th><th>No ano (%)</th><th>12 meses (%)</th></tr>
              </thead>
              <tbody>
                {invertido.map((row) => (
                  <tr key={row.mes}>
                    <td>{row.mes}</td>
                    <td>{row.indice?.toLocaleString("pt-BR", { minimumFractionDigits: 3 })}</td>
                    <td className={row.variacao?.no_mes > 0 ? "incc-pos" : "incc-neg"}>
                      {row.variacao?.no_mes != null ? `${row.variacao.no_mes > 0 ? "+" : ""}${row.variacao.no_mes.toFixed(2)}` : "—"}
                    </td>
                    <td>{row.variacao?.no_ano != null ? row.variacao.no_ano.toFixed(2) : "—"}</td>
                    <td>{row.variacao?.doze_meses != null ? row.variacao.doze_meses.toFixed(2) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="incc-dropdown__fonte">Fonte: FGV / Sinduscon-PR</p>
        </div>
      )}
    </div>
  );
}

// ── Histórico da calculadora (localStorage) ──
const HISTORICO_CALC_KEY = "incc_calc_historico";
const HISTORICO_CALC_MAX = 50;

function lerHistoricoCalc() {
  try {
    return JSON.parse(localStorage.getItem(HISTORICO_CALC_KEY)) || [];
  } catch {
    return [];
  }
}

function salvarHistoricoCalc(lista) {
  try {
    localStorage.setItem(HISTORICO_CALC_KEY, JSON.stringify(lista));
  } catch {}
}

const brl = (n) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function CalculadoraWindow({ historico, onClose }) {
  const [pos, setPos] = useState({
    x: Math.max(0, window.innerWidth / 2 - 220),
    y: Math.max(0, window.innerHeight / 2 - 260),
  });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const [valor, setValor] = useState("");
  const [mesIni, setMesIni] = useState("");
  const [anoIni, setAnoIni] = useState("");
  const [mesFi, setMesFi] = useState("");
  const [anoFi, setAnoFi] = useState("");
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState("");
  const [historicoCalc, setHistoricoCalc] = useState(lerHistoricoCalc);

  const onMouseDown = useCallback((e) => {
    if (e.target.closest(".calc-window__close")) return;
    setDragging(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragStart.current.mx;
      const dy = e.clientY - dragStart.current.my;
      setPos({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  // ── Anos disponíveis (derivados do dataRef, uniforme em toda a tabela) ──
  const anos = [...new Set(historico.map((r) => r.dataRef?.slice(0, 4)).filter(Boolean))].reverse();

  const buscarRow = (m, a) =>
    historico.find((r) => r.dataRef === `${a}-${String(Number(m) + 1).padStart(2, "0")}-01`);

  function atualizarHistorico(lista) {
    setHistoricoCalc(lista);
    salvarHistoricoCalc(lista);
  }

  function calcular() {
    setErro("");
    setResultado(null);
    const valorNum = parseFloat(valor.replace(/\./g, "").replace(",", "."));
    if (!valorNum || isNaN(valorNum)) return setErro("Informe um valor válido.");
    if (mesIni === "" || !anoIni) return setErro("Selecione mês e ano de início.");
    if (mesFi === "" || !anoFi) return setErro("Selecione mês e ano de fim.");
    const rowInicio = buscarRow(mesIni, anoIni);
    const rowFim = buscarRow(mesFi, anoFi);
    if (!rowInicio?.indice || !rowFim?.indice) return setErro("Índice não disponível para o período selecionado.");
    if (rowInicio.dataRef >= rowFim.dataRef) return setErro("O início deve ser anterior ao fim.");
    const fator = rowFim.indice / rowInicio.indice;
    const valorCorrigido = valorNum * fator;
    const variacao = (fator - 1) * 100;
    const res = {
      valorCorrigido, fator, variacao,
      indiceInicio: rowInicio.indice, indiceFim: rowFim.indice,
      mesInicio: rowInicio.mes, mesFim: rowFim.mes,
    };
    setResultado(res);
    atualizarHistorico([
      {
        id: Date.now(),
        valor, valorNum, mesIni, anoIni, mesFi, anoFi,
        ...res,
        data: new Date().toISOString(),
      },
      ...historicoCalc,
    ].slice(0, HISTORICO_CALC_MAX));
  }

  function carregarDoHistorico(h) {
    setValor(h.valor);
    setMesIni(h.mesIni);
    setAnoIni(h.anoIni);
    setMesFi(h.mesFi);
    setAnoFi(h.anoFi);
    setErro("");
    setResultado({
      valorCorrigido: h.valorCorrigido, fator: h.fator, variacao: h.variacao,
      indiceInicio: h.indiceInicio, indiceFim: h.indiceFim,
      mesInicio: h.mesInicio, mesFim: h.mesFim,
    });
  }

  function removerDoHistorico(id) {
    atualizarHistorico(historicoCalc.filter((h) => h.id !== id));
  }

  function limparHistorico() {
    atualizarHistorico([]);
  }

  return (
    <div className="calc-window" style={{ left: pos.x, top: pos.y, cursor: dragging ? "grabbing" : "default" }}>
      <div className="calc-window__titlebar" onMouseDown={onMouseDown} style={{ cursor: dragging ? "grabbing" : "grab" }}>
        <div className="calc-window__title">
          <FontAwesomeIcon icon={faGripVertical} className="calc-window__grip" />
          Calculadora INCC
        </div>
        <button className="calc-window__close" onClick={onClose} title="Fechar">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className="calc-window__body">
        <div className="incc-calc__field">
          <label>Valor original (R$)</label>
          <input type="text" placeholder="Ex: 350.000,00" value={valor} onChange={(e) => setValor(e.target.value)} className="incc-calc__input" onKeyDown={(e) => e.key === "Enter" && calcular()} />
        </div>

        <div className="incc-calc__row">
          <div className="incc-calc__field">
            <label>Início</label>
            <div className="incc-calc__pair">
              <select value={mesIni} onChange={(e) => setMesIni(e.target.value)} className="incc-calc__input">
                <option value="">Mês</option>
                {MESES_PT.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={anoIni} onChange={(e) => setAnoIni(e.target.value)} className="incc-calc__input">
                <option value="">Ano</option>
                {anos.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="incc-calc__arrow" />
          <div className="incc-calc__field">
            <label>Fim</label>
            <div className="incc-calc__pair">
              <select value={mesFi} onChange={(e) => setMesFi(e.target.value)} className="incc-calc__input">
                <option value="">Mês</option>
                {MESES_PT.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={anoFi} onChange={(e) => setAnoFi(e.target.value)} className="incc-calc__input">
                <option value="">Ano</option>
                {anos.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
        </div>

        {erro && <p className="incc-calc__erro">{erro}</p>}

        <button className="incc-calc__btn" onClick={calcular}>Calcular correção</button>

        {resultado && (
          <div className="incc-calc__resultado">
            <div className="incc-calc__resultado-row">
              <span>Índice início ({resultado.mesInicio})</span>
              <strong>{resultado.indiceInicio.toLocaleString("pt-BR", { minimumFractionDigits: 3 })}</strong>
            </div>
            <div className="incc-calc__resultado-row">
              <span>Índice fim ({resultado.mesFim})</span>
              <strong>{resultado.indiceFim.toLocaleString("pt-BR", { minimumFractionDigits: 3 })}</strong>
            </div>
            <div className="incc-calc__resultado-row">
              <span>Fator de correção</span>
              <strong>{resultado.fator.toFixed(6)}</strong>
            </div>
            <div className="incc-calc__resultado-row">
              <span>Variação no período</span>
              <strong className={resultado.variacao >= 0 ? "incc-calc__pos" : "incc-calc__neg"}>
                {resultado.variacao > 0 ? "+" : ""}{resultado.variacao.toFixed(4)}%
              </strong>
            </div>
            <div className="incc-calc__resultado-destaque">
              <span>Valor corrigido</span>
              <strong>{resultado.valorCorrigido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
            </div>
          </div>
        )}

        {historicoCalc.length > 0 && (
          <div className="incc-calc__historico">
            <div className="incc-calc__historico-header">
              <span>Histórico</span>
              <button onClick={limparHistorico}>Limpar</button>
            </div>
            <div className="incc-calc__historico-lista">
              {historicoCalc.map((h) => (
                <div key={h.id} className="incc-calc__historico-item" onClick={() => carregarDoHistorico(h)} title="Clique para reabrir">
                  <div className="incc-calc__historico-info">
                    <span className="incc-calc__historico-periodo">{h.mesInicio} → {h.mesFim}</span>
                    <span className="incc-calc__historico-valores">{brl(h.valorNum)} → <strong>{brl(h.valorCorrigido)}</strong></span>
                    <span className="incc-calc__historico-data">{new Date(h.data).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); removerDoHistorico(h.id); }} title="Remover">×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="incc-calc__fonte">Fonte: FGV / Sinduscon-PR</p>
      </div>
    </div>
  );
}

function getSaudacao() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

// ── Monta a frase do cx-greeting ──
function getGreetingSub({ isOwnContainer, hasProjects, hasSetores, ownerNome }) {
  const nome = ownerNome || "usuário";

  if (isOwnContainer) {
    if (hasProjects && hasSetores) return "Acompanhe seus projetos e setores";
    if (hasProjects) return "Acompanhe seus projetos";
    if (hasSetores) return "Acompanhe seus setores";
    return "Acompanhe seus projetos e setores";
  }

  if (hasProjects && hasSetores) return `Acompanhe os projetos e setores de ${nome}`;
  if (hasProjects) return `Acompanhe os projetos de ${nome}`;
  if (hasSetores) return `Acompanhe os setores de ${nome}`;
  return `Acompanhe os projetos e setores de ${nome}`;
}

// ─────────────────────────────────────────────
// CONTAINERS — página principal
// ─────────────────────────────────────────────
export default function Containers({ containerIdDaUrl }) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [ownProfile, setOwnProfile] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [containerAtual, setContainerAtual] = useState(null);
  const [loading, setLoading] = useState(true);

  const [inccUltimo, setInccUltimo] = useState(null);
  const [inccHistorico, setInccHistorico] = useState([]);
  const [showCalculadora, setShowCalculadora] = useState(false);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);
  const [busca, setBusca] = useState("");

  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifPanelRef = useRef(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // ── Novidades Modal ──
  const [showNovidadesAuto, setShowNovidadesAuto] = useNovidadesAutoShow();
  const [showNovidadesManual, setShowNovidadesManual] = useState(false);
  const showNovidades = showNovidadesAuto || showNovidadesManual;

  const fecharNovidades = () => {
    setShowNovidadesAuto(false);
    setShowNovidadesManual(false);
  };

  const [sidebarProps, setSidebarProps] = useState({
    projects: [],
    setores: [],
    workspaces: [],
    selectedProject: null,
    onCreateProject: () => {},
    onProjectSelect: () => {},
    onDeleteProject: () => {},
    onOpenSetoresManager: () => {},
    currentUserId: null,
    containerOwnerId: null,
    gerenteContainerId: null,
    currentContainerId: null,
    layoutModo: "linhas",
    onToggleLayoutModo: () => {},
    onToggleBackgroundMenu: () => {},
  });

  // ── Fecha dropdowns ao clicar fora ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) setShowNotifPanel(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Carrega INCC ──
  useEffect(() => {
    let cancelado = false;

    async function carregarIncc() {
      try {
        const historico = await fetchInccDoSupabase();
        if (cancelado) return;

        if (historico.length > 0) {
          setInccHistorico(historico);
          setInccUltimo(historico[historico.length - 1]);
        }

        const ultimoMes = historico.length > 0 ? historico[historico.length - 1].mes : null;

        sincronizarInccDaApi(ultimoMes).then(async () => {
          if (cancelado) return;
          try {
            const atualizado = await fetchInccDoSupabase();
            if (cancelado) return;
            if (atualizado.length > historico.length) {
              setInccHistorico(atualizado);
              setInccUltimo(atualizado[atualizado.length - 1]);
            }
          } catch { /* ignora */ }
        });
      } catch (err) {
        console.warn("Erro ao carregar INCC do Supabase:", err);
      }
    }

    carregarIncc();
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) setUser(session.user);
      else navigate("/login", { replace: true });
      setLoading(false);
    };
    loadUser();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) setUser(session.user);
      else navigate("/login", { replace: true });
    });

    return () => listener.subscription.unsubscribe();
}, [navigate]);

  // ── Perfil do usuário logado ──
  const loadOwnProfile = useCallback(async () => {
  if (!user?.id) return;
  const { data } = await supabase
    .from("profiles")
    .select("nome, avatar_url, empresa, funcao, container, telefone")
    .eq("id", user.id)
    .maybeSingle();
  setOwnProfile(data || null);
}, [user?.id]);

  useEffect(() => { loadOwnProfile(); }, [loadOwnProfile]);

  // ── Auto-abrir perfil se incompleto ──
// ── Banner de primeiro acesso ──
useEffect(() => {
  if (user?.id) {
    const incompleto = !ownProfile?.nome || !ownProfile?.empresa || !ownProfile?.funcao || !ownProfile?.container;
    setShowOnboarding(incompleto);
  }
}, [ownProfile, user?.id]);

const handleAbrirPerfilOnboarding = () => {
  setShowOnboarding(false);
  setShowProfileCard(true);
};

  useEffect(() => {
    const idParaUsar = containerIdDaUrl || localStorage.getItem("containerAtual") || user?.id;
    setContainerAtual(idParaUsar);
  }, [user, containerIdDaUrl]);

  useEffect(() => {
    if (containerAtual) {
      localStorage.setItem("containerAtual", containerAtual);
      setSidebarProps((prev) => ({ ...prev, currentContainerId: containerAtual }));
    }
  }, [containerAtual]);

  // ── Nome do dono do container visitado (quando não é o próprio) ──
  const isOwnContainer = !!user?.id && containerAtual === user.id;

  useEffect(() => {
    const loadOwnerProfile = async () => {
      if (!containerAtual || isOwnContainer) {
        setOwnerProfile(null);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("nome")
        .eq("id", containerAtual)
        .maybeSingle();
      setOwnerProfile(data || null);
    };
    loadOwnerProfile();
  }, [containerAtual, isOwnContainer]);

  // ════════════════════════════════════════════════════════════════════════
  // ── Contagem de notificações não lidas (independente do painel estar aberto) ──
  // ════════════════════════════════════════════════════════════════════════
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id || !user?.email) return;
    try {
      const [{ count: convitesCount }, { count: mencoesCount }] = await Promise.all([
        supabase.from("convites")
          .select("id", { count: "exact", head: true })
          .eq("email", user.email)
          .eq("status", "pendente"),
        supabase.from("notificacoes")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("lido", false),
      ]);
      setNotificacoesNaoLidas((convitesCount || 0) + (mencoesCount || 0));
    } catch (err) {
      console.error("Erro ao contar notificações:", err);
    }
  }, [user?.id, user?.email]);

  // Busca a contagem ao montar o componente
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // ── Atualiza em tempo real quando chega notificação nova ──
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`notificacoes-badge-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notificacoes", filter: `user_id=eq.${user.id}` },
        () => fetchUnreadCount()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchUnreadCount]);

  // ════════════════════════════════════════════════════════════════════════

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (loading) return <Loading />;

  const primeiroNome = ownProfile?.nome?.split(" ")[0] || "";
  const ownerPrimeiroNome = ownerProfile?.nome?.split(" ")[0] || "";
  const hasProjects = (sidebarProps.projects?.length || 0) > 0;
  const hasSetores = (sidebarProps.setores?.length || 0) > 0;

  const greetingSub = getGreetingSub({
    isOwnContainer,
    hasProjects,
    hasSetores,
    ownerNome: ownerPrimeiroNome,
  });

  return (
    <div className="containers-page">
      {showOnboarding && (
        <div className="cx-onboarding-overlay" onClick={handleAbrirPerfilOnboarding}>
          <div className="cx-onboarding-modal" onClick={e => e.stopPropagation()}>
            <div className="cx-onboarding-emoji">👋</div>
            <h2 className="cx-onboarding-titulo">Bem-vindo!</h2>
            <p className="cx-onboarding-texto">
              Para começar, preencha as informações do seu perfil clicando no botão no canto superior direito.
            </p>
            <button className="cx-onboarding-btn" onClick={handleAbrirPerfilOnboarding}>
              Completar meu cadastro
            </button>
          </div>
        </div>
      )}
      <div className="containers-content">

        <ContainersSidebar
          user={user}
          containerAtual={containerAtual}
          setContainerAtual={setContainerAtual}
          projects={sidebarProps.projects}
          selectedProject={sidebarProps.selectedProject}
          onProjectSelect={sidebarProps.onProjectSelect}
          onCreateProject={sidebarProps.onCreateProject}
          onOpenSetoresManager={sidebarProps.onOpenSetoresManager}
          onOpenNovidades={() => setShowNovidadesManual(true)}
        />

        <div className="containers-main-with-title">
          <div className="cx-topbar">
            <div className="cx-topbar__search">
              <input
                type="text"
                placeholder="Buscar projetos, setores, documentos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
              <button
                className="cx-topbar__search-btn"
                title="Buscar"
                onClick={(e) => e.currentTarget.previousSibling?.focus()}
              >
                <FaSearch size={13} />
              </button>
            </div>

            <div className="cx-topbar__spacer" />

            <div className="cx-topbar__bell-wrap" ref={notifPanelRef}>
              <button
                className="cx-topbar__bell"
                title="Notificações"
                onClick={() => setShowNotifPanel((v) => !v)}
              >
                <FaBell size={16} />
                {notificacoesNaoLidas > 0 && (
                  <span className="cx-topbar__bell-badge">{notificacoesNaoLidas > 9 ? "9+" : notificacoesNaoLidas}</span>
                )}
              </button>

              {showNotifPanel && (
                <NotificationsPanel
                  user={user}
                  containerAtual={containerAtual}
                  setContainerAtual={setContainerAtual}
                  onClose={() => setShowNotifPanel(false)}
                />
              )}
            </div>

            <div className="cx-topbar__profile">
              <button className="cx-topbar__profile-btn" onClick={() => setShowProfileCard(true)}>
                <div className="cx-topbar__avatar">
                  {ownProfile?.avatar_url ? <img src={ownProfile.avatar_url} alt={ownProfile?.nome} /> : primeiroNome.charAt(0)?.toUpperCase() || "?"}
                </div>
                <span className="cx-topbar__profile-name">{primeiroNome}</span>
              </button>
            </div>
          </div>

          <div className="cx-greeting">
            <span className="cx-greeting__sub">{greetingSub}</span>

            <div className="cx-greeting__right">
              <INCCBadge incc={inccUltimo} historico={inccHistorico} />
              <button
                className={`cx-greeting__calc ${showCalculadora ? "cx-greeting__calc--active" : ""}`}
                onClick={() => setShowCalculadora((v) => !v)}
                title="Calculadora de correção pelo INCC"
              >
                <FontAwesomeIcon icon={faCalculator} />
              </button>

              <span className="cx-greeting__divider" />

              <button
                className="cx-greeting__layout-btn"
                onClick={sidebarProps.onToggleLayoutModo}
                title={sidebarProps.layoutModo === "colunas" ? "Ver em linhas" : "Ver em colunas"}
              >
                {sidebarProps.layoutModo === "colunas" ? <FaBars size={14} /> : <FaColumns size={14} />}
              </button>

              <button
                className="cx-greeting__dots-btn"
                onClick={sidebarProps.onToggleBackgroundMenu}
                title="Personalizar fundo"
              >
                ⋮
              </button>
            </div>
          </div>

          <ProjectManager
            containerAtual={containerAtual}
            user={user}
            onSidebarUpdate={setSidebarProps}
            searchTerm={busca}
          />
        </div>
      </div>

            {showProfileCard && (
        <UserProfileCard
          userId={user?.id}
          userEmail={user?.email}
          profile={ownProfile}
          onProfileUpdate={setOwnProfile}
          onLogout={() => navigate("/")}
          onClose={() => setShowProfileCard(false)}
        />
      )}

      {showCalculadora && (
        <CalculadoraWindow
          historico={inccHistorico}
          onClose={() => setShowCalculadora(false)}
        />
      )}

      <NovidadesModal isOpen={showNovidades} onClose={fecharNovidades} />
    </div>
  );
}